import React, { useEffect } from "react";
import { ExternalLink, ImageIcon, Network, Globe, Code2, ArrowRightLeft, Eye, ShieldCheck } from "lucide-react";
import Footer from "../components/Footer";

const tools = [
  {
    id: "image-forensics",
    name: "Exif Viewer",
    description:
      "Extract sub-surface EXIF metadata, camera specs, and GPS coordinates from image files to reconstruct device fingerprints and forensic evidence.",
    icon: ImageIcon,
    tags: ["EXIF", "GPS", "Metadata", "Camera"],
    color: "#00D4FF",
    url: "https://exif.traxelon.com",
    badge: "Client-Side",
  },
  {
    id: "header-analyzer",
    name: "Email Header Analyzer",
    description:
      "Deconstruct email headers and routing paths to uncover true message origins, relay servers, and sender authentication status.",
    icon: Network,
    tags: ["Email", "SMTP", "Routing", "SPF/DKIM"],
    color: "#A78BFA",
    url: "https://header.traxelon.com",
    badge: "Client-Side",
  },
  {
    id: "subdomain-finder",
    name: "Subdomain Enumerator",
    description:
      "Discover hidden subdomains of any target domain. Map attack surfaces and expose shadow infrastructure with passive reconnaissance.",
    icon: Globe,
    tags: ["Recon", "DNS", "Attack Surface"],
    color: "#34D399",
    url: "https://sub.traxelon.com",
    badge: "API-Powered",
  },
  {
    id: "tech-stack",
    name: "Tech Stack Detector",
    description:
      "Identify the technologies, frameworks, CMS, CDNs, and infrastructure powering any website with a single URL scan.",
    icon: Code2,
    tags: ["CMS", "Framework", "CDN", "Fingerprint"],
    color: "#FB923C",
    url: "https://built.traxelon.com",
    badge: "API-Powered",
  },
  {
    id: "data-converter",
    name: "Data Encoder / Decoder",
    description:
      "Convert and decode strings across Base64, Base32, Hex, Binary, URL encoding, HTML Entities, ROT13, ROT47 and more.",
    icon: ArrowRightLeft,
    tags: ["Base64", "Hex", "Binary", "Encoding"],
    color: "#F472B6",
    url: "https://crypt.traxelon.com",
    badge: "Client-Side",
  },
  {
    id: "steganography",
    name: "Steganographer",
    description:
      "Embed hidden messages and files within images using LSB encoding. Extract and reveal concealed data from suspicious image files.",
    icon: Eye,
    tags: ["LSB", "Covert", "Hidden Data", "Image"],
    color: "#FBBF24",
    url: "https://steg.traxelon.com",
    badge: "Client-Side",
  },
  {
    id: "password-checker",
    name: "Password Strength Auditor",
    description:
      "Analyze password complexity, estimate brute-force cracking time, and verify against leaked credentials databases without sending raw data.",
    icon: ShieldCheck,
    tags: ["Security", "Audit", "Entropy", "Pwned Check"],
    color: "#10B981",
    url: "https://password.traxelon.com",
    badge: "Client-Side",
  },
];

function ToolCard({ tool }) {
  const Icon = tool.icon;

  const handleOpen = () => {
    window.open(tool.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="group relative bg-surface-card border border-surface-border rounded-2xl p-6 flex flex-col gap-4 cursor-pointer transition-all duration-300 hover:border-opacity-80 hover:-translate-y-1 overflow-hidden"
      style={{
        "--tool-color": tool.color,
      }}
      onClick={handleOpen}
    >
      {/* Background glow on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 20% 20%, ${tool.color}12 0%, transparent 60%)`,
          borderColor: `${tool.color}40`,
        }}
      />

      {/* Animated border on hover */}
      <div
        className="absolute inset-0 rounded-2xl border opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ borderColor: `${tool.color}50` }}
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 relative z-10">
        <div
          className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
          style={{
            background: `${tool.color}18`,
            border: `1px solid ${tool.color}35`,
          }}
        >
          <Icon
            className="w-5 h-5 transition-colors duration-300"
            style={{ color: tool.color }}
          />
        </div>

        <span
          className="text-xs font-mono px-2 py-1 rounded-full border font-medium flex-shrink-0"
          style={{
            color: tool.color,
            borderColor: `${tool.color}40`,
            background: `${tool.color}10`,
          }}
        >
          {tool.badge}
        </span>
      </div>

      {/* Title + Description */}
      <div className="relative z-10 flex-1">
        <h3 className="font-display text-lg text-white tracking-wide mb-2">
          {tool.name}
        </h3>
        <p className="font-body text-sm text-text-secondary leading-relaxed">
          {tool.description}
        </p>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 relative z-10">
        {tool.tags.map((tag) => (
          <span
            key={tag}
            className="text-xs font-mono px-2 py-0.5 rounded-md text-text-muted bg-surface border border-surface-border"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Launch button */}
      <button
        className="relative z-10 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border font-body text-sm font-semibold transition-all duration-300 group-hover:shadow-md mt-1"
        style={{
          color: tool.color,
          borderColor: `${tool.color}50`,
          background: `${tool.color}10`,
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleOpen();
        }}
      >
        Launch Tool
        <ExternalLink className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function Tools() {
  useEffect(() => {
    document.title = "Forensic Tools Suite — Traxelon Investigative Toolkit";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Traxelon Forensic Tools: EXIF image analyzer, email header tracer, subdomain enumerator, steganography detector, crypto toolkit, tech stack scanner, and password strength auditor for cybercrime investigators.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://traxelon.com/tools");
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", "https://traxelon.com/tools");
  }, []);

  return (
    <div className="min-h-screen bg-surface pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-xs text-primary tracking-widest uppercase">
              {tools.length} Tools Available
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-wider uppercase mb-4">
            Forensic{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #00D4FF, #A78BFA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Toolkit
            </span>
          </h1>
          <p className="font-body text-text-secondary max-w-2xl mx-auto text-base leading-relaxed">
            Advanced digital forensic tools to analyze files, reconstruct metadata, and extract
            actionable intelligence. Each tool opens as its own dedicated application.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool) => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-14 text-center">
          <p className="font-body text-xs text-text-muted">
            All tools open in a new tab as standalone applications.{" "}
            <span className="text-primary/70">No login required.</span>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
